import { Box, Button, Card, CardHeader, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionSkeleton,
  AccordionTitle,
  CreateIssueModal,
  Issue,
  IssueDrawerContent,
  IssuesTable,
  MaterialSymbolIcon,
  MaterialSymbols,
  SignalRGroup,
  useDrawer,
  ViewList,
} from "../../../../components";
import { ISSUE_STATUSES, TeamIssuesGroupByOption } from "../../../../constants";
import { useSession, useSignalR } from "../../../../hooks";
import { IssueSummaryDto, queryKeys, useGetIssues, useGetTeam } from "../../../../http";
import { useTeamImplementationSettings } from "../hooks";
import { TeamIssuesFilterButton } from "./TeamIssuesFilterButton";

interface IProps {
  teamSlug: string;
  isTableView: boolean;
}

const TeamIssuesList = (props: IProps) => {
  const { userId: sessionUserGuid } = useSession();
  const { settings } = useTeamImplementationSettings(props.teamSlug);
  const { data: team } = useGetTeam(props.teamSlug, sessionUserGuid);
  const { data, isLoading } = useGetIssues(props.teamSlug, undefined, {
    filters: {
      status: settings.issuesFilterByStatusOptions?.map((x) => x.toString()) ?? null,
    },
    sortProperty: settings.issuesSortByOption,
    sortDirection: settings.issuesSortByDirection,
  });
  const [groupedIssues, setGroupedIssues] = useState<Map<string, IssueSummaryDto[]> | undefined>(undefined);
  const [isCreateIssueModalOpen, setIsCreateIssueModalOpen] = useState(false);
  const { showDrawer } = useDrawer();
  const signalR = useSignalR();
  const queryClient = useQueryClient();
  const theme = useTheme();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.TeamIssues(props.teamSlug));
    return () => signalR.leaveGroup(SignalRGroup.TeamIssues(props.teamSlug));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.teamSlug]);

  useEffect(() => {
    const handleShouldRefetchTeamIssues = (teamSlug: string) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssues(teamSlug, undefined));
    };

    signalR.onShouldRefetchTeamIssues(handleShouldRefetchTeamIssues);
    return () => signalR.offShouldRefetchTeamIssues(handleShouldRefetchTeamIssues);
  }, [queryClient, signalR]);

  const groupByFunction = useCallback(
    (issue: IssueSummaryDto) => {
      switch (settings.issuesGroupByOption) {
        case TeamIssuesGroupByOption.Status:
          return ISSUE_STATUSES.find((x) => x.id === issue.currentStatus)?.name;
        case TeamIssuesGroupByOption.CreatedBy:
          return issue.createdByUser.displayName;
        case TeamIssuesGroupByOption.None:
        default:
          return "All";
      }
    },
    [settings.issuesGroupByOption],
  );

  const groupSortByFunction = useCallback(
    (groupName: string) => {
      switch (settings.issuesGroupByOption) {
        case TeamIssuesGroupByOption.Status:
          return _.sortBy(ISSUE_STATUSES, (x) => x.sortOrder)
            .map((x) => x.name)
            .indexOf(groupName);
        case TeamIssuesGroupByOption.CreatedBy:
        case TeamIssuesGroupByOption.None:
        default:
          return groupName;
      }
    },
    [settings.issuesGroupByOption],
  );

  useEffect(() => {
    if (isLoading) {
      // Clear local state when loading new data
      setGroupedIssues(undefined);
    }
  }, [isLoading]);

  useEffect(() => {
    if (data !== undefined) {
      // Group issues
      const issues = _.groupBy(data.issues, groupByFunction);

      // Sort issue group names
      const sortedGroupNames = _.sortBy(_.keys(issues), groupSortByFunction);

      // Create a map of sorted groups
      const sortedGroupedIssues = sortedGroupNames.reduce((groupedIssues, groupName) => {
        groupedIssues.set(groupName, issues[groupName]);
        return groupedIssues;
      }, new Map());

      setGroupedIssues(sortedGroupedIssues);
    }
  }, [data, groupByFunction, groupSortByFunction]);

  async function handleIssueClicked(issue: IssueSummaryDto) {
    showDrawer({
      content: <IssueDrawerContent issueGuid={issue.guid} />,
    });
  }

  return (
    <Card>
      <CardHeader
        direction="row"
        title={<Typography variant="h6">Loops</Typography>}
        action={
          <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
            <Button
              variant="tertiary"
              disabled={isLoading}
              startIcon={<MaterialSymbolIcon symbol={MaterialSymbols.QuickPhrases} />}
              onClick={() => setIsCreateIssueModalOpen(true)}
            >
              Add New Loop
            </Button>
            <TeamIssuesFilterButton teamSlug={props.teamSlug} disabled={isLoading} />
          </Stack>
        }
        sx={{ pb: 0 }}
      />
      <Box sx={{ p: 0.5 }}>
        {isLoading || team === undefined || groupedIssues === undefined ? (
          <AccordionSkeleton isTableView={props.isTableView} />
        ) : (
          <Stack spacing={0.5}>
            {groupedIssues.size === 0 ? (
              <Typography variant="placeholder" sx={{ px: 0.5 }}>
                Nothing here yet.
              </Typography>
            ) : (
              Array.from(groupedIssues.keys()).map((groupName, i) => (
                <Accordion
                  key={i}
                  title={(expanded) => (
                    <AccordionTitle
                      title={groupName}
                      subtitle={expanded ? undefined : `(${groupedIssues.get(groupName)?.length})`}
                    />
                  )}
                  color={theme.palette.secondary.main}
                  accordionDetailsSx={{ px: props.isTableView ? 0 : 0.5, pb: props.isTableView ? 0 : 0.5 }}
                >
                  {props.isTableView ? (
                    <IssuesTable
                      tableId={`teamIssuesList-${team.slug}`}
                      issues={groupedIssues.get(groupName) ?? []}
                      enableActions={team.isAdministrator || team.isMember}
                    />
                  ) : (
                    <ViewList
                      dataSource={groupedIssues.get(groupName)}
                      renderItem={(issue) => (
                        <Issue
                          key={issue.guid}
                          issue={issue}
                          onClick={() => handleIssueClicked(issue)}
                          isFocused={false}
                          sx={{ px: 0.5 }}
                        />
                      )}
                    />
                  )}
                </Accordion>
              ))
            )}
          </Stack>
        )}
      </Box>

      <CreateIssueModal
        visible={isCreateIssueModalOpen}
        onCreated={() => setIsCreateIssueModalOpen(false)}
        onCancelled={() => setIsCreateIssueModalOpen(false)}
        defaultSelectedTeamSlug={props.teamSlug}
      />
    </Card>
  );
};

export { TeamIssuesList };
